import * as Sentry from '@sentry/react';
import { containsRedactedPath } from '@ruby/observability/redaction';
import { CHUNK_LOAD_PATTERNS } from './handle-chunk-load-failure';

/**
 * Initialize Sentry in the React client. Safe to call before DSN is configured
 * — returns early when `import.meta.env.VITE_SENTRY_DSN` is empty.
 *
 * PHI scrubbing is bundled in `beforeSend` so no path where a patient form
 * submission crashes can leak DOB/HC/phone to Sentry. If the walker finds any
 * PHI field name in the event payload it drops the event rather than risk
 * partial scrubbing.
 *
 * `containsRedactedPath` + `PHI_KEY_NAMES` are imported from the `/redaction`
 * subpath — pure-logic module with no node deps, safe in browser bundles. The
 * main `@ruby/observability` entry would drag pino in; this subpath won't.
 *
 * Call this exactly once at app startup, before `createRoot`.
 */
export function initSentryClient(): void {
  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined) || '';
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: resolveEnvironment(),
    release: (import.meta.env.VITE_GIT_SHA as string | undefined) || 'unknown',
    tracesSampleRate: 0, // performance tracing deferred — separate project
    // Stale-bundle dynamic-import failures auto-recover via the onError hook
    // wired on every ErrorBoundary (see handle-chunk-load-failure.ts). They
    // aren't bugs — just a tab on an old hash hitting the new CDN — so drop
    // them at the SDK before they ship and noise up the issue list.
    // Each substring below is a literal copy of a server-side friendly
    // string (admin-api.ts FRIENDLY_USER_NOT_FOUND_MESSAGE,
    // auth/users.ts SELF_USER_GONE_MESSAGE, and the dup-collision strings
    // in friendlyUserCollisionMessage). The server returns them as
    // `body.error`; `lib/api.ts:apiFetch` wraps them in `ApiError.message`;
    // React SDK matches against the message via substring. Touching any
    // of these strings on the server requires a paired edit here, or the
    // benign-race events start flooding Sentry again.
    ignoreErrors: [
      // Spread from the recovery helper's own list rather than restating it.
      // These two were hand-copied and then DRIFTED: `b1194c86` (2026-06-30)
      // added the CSS-preload pattern to `CHUNK_LOAD_PATTERNS` for the reload,
      // but not here — so a stale-CSS chunk miss self-healed exactly as designed
      // and STILL shipped an issue to Sentry (RUBY-21, 2026-08-07, on a day with
      // four hq-bundle deploys). One list, one source of truth.
      // Plan: docs/plans/2026-08-08-prod-audit-fixes.md
      ...CHUNK_LOAD_PATTERNS,
      // Admin acting on a row that another admin tab already deleted
      // (RUBY-H, 2026-05-29).
      'This user no longer exists',
      // Self-service routes when the actor's own account got terminated
      // mid-session.
      'Your account is no longer active',
      // Admin create / update / invite hitting an email or username
      // collision — RUBY-15 surface. Server returns a friendly 409.
      'This email is already in use by another account',
      'This username is already taken',
    ],
    // Console lines can carry PHI the key-name walker below never sees (it
    // matches field NAMES, and a console breadcrumb is free text), so they
    // never ride on an event — the same rule as the server's initSentry.
    // Plan: docs/plans/BUG-A1-2026-09-24-hcv-proxy-exception-filed-as-refusal.md
    beforeBreadcrumb(breadcrumb) {
      return breadcrumb.category === 'console' ? null : breadcrumb;
    },
    beforeSend(event) {
      if (containsRedactedPath(event)) return null;
      return event;
    },
  });
}

/**
 * One ruby-hq image serves both envs (the client bundle is baked once), so
 * infer Sentry environment from the hostname at runtime instead of MODE.
 * Falls back to Vite's MODE for local dev.
 */
function resolveEnvironment(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'myruby.me' || host.endsWith('.myruby.me')) return 'production';
    if (host === 'rubyhq.cloud' || host.endsWith('.rubyhq.cloud')) return 'staging';
  }
  return (import.meta.env.MODE as string) || 'development';
}

export { Sentry };
